import React from 'react';

const MethodologySection = () => {
  return (
    <section className="py-16 px-4 md:px-8 lg:px-16 bg-white">
      <div className="max-w-7xl mx-auto">
        {/* En-tête de section */}
        <div className="text-center mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-dark-1 mb-4 font-sans">
            Notre méthodologie pédagogique innovante
          </h2>
          <p className="text-light-1 text-lg max-w-4xl mx-auto font-sans">
            GoNextMaker adopte une approche active et orientée <span className="font-bold text-purple-1">création</span>, 
            déployée sous <span className="font-bold text-blue-1">format bootcamp</span> combinant des sessions hybrides (présentiel + en ligne).
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          {/* Principes pédagogiques */}
          <div>
            <h3 className="text-xl font-bold text-dark-1 mb-6 font-sans">Notre pédagogie repose sur :</h3>
            <div className="space-y-6">
              <div className="flex items-start">
                <div className="w-12 h-12 bg-green-1 rounded-full flex items-center justify-center text-dark-2 text-xl font-bold mr-4 flex-shrink-0">
                  1
                </div>
                <div>
                  <h4 className="font-bold text-dark-1 mb-2 font-sans">Apprentissage par projet et par la pratique</h4>
                  <p className="text-light-1 font-sans">
                    Les apprenants conçoivent très tôt des projets concrets et des prototypes.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-12 h-12 bg-blue-1 rounded-full flex items-center justify-center text-white text-xl font-bold mr-4 flex-shrink-0">
                  2
                </div>
                <div>
                  <h4 className="font-bold text-dark-1 mb-2 font-sans">Pédagogie maker</h4>
                  <p className="text-light-1 font-sans">
                    Valoriser la fabrication (physique ou numérique) comme moteur d'apprentissage.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-12 h-12 bg-purple-1 rounded-full flex items-center justify-center text-white text-xl font-bold mr-4 flex-shrink-0">
                  3
                </div>
                <div>
                  <h4 className="font-bold text-dark-1 mb-2 font-sans">Collaboration & tutorat</h4>
                  <p className="text-light-1 font-sans">
                    Échanges entre pairs, accompagnement personnalisé, mentorat inspirant.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-12 h-12 bg-red-4 rounded-full flex items-center justify-center text-white text-xl font-bold mr-4 flex-shrink-0">
                  4
                </div>
                <div>
                  <h4 className="font-bold text-dark-1 mb-2 font-sans">Système adaptatif</h4>
                  <p className="text-light-1 font-sans">
                    Auto-évaluation initiale permettant d'ajuster modules et activités au profil et au rythme de chaque apprenant.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Illustration méthodologie */}
          <div className="bg-gradient-to-br from-light-2 to-light-3 rounded-2xl p-8 flex items-center justify-center">
            <div className="text-center">
              <div className="text-6xl mb-4">🚀</div>
              <h3 className="text-xl font-bold text-dark-1 mb-4 font-sans">Format Bootcamp Intensif</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white rounded-lg p-3 text-center">
                  <div className="text-2xl">🖥️</div>
                  <p className="text-sm font-sans text-dark-1">Sessions en ligne</p>
                </div>
                <div className="bg-white rounded-lg p-3 text-center">
                  <div className="text-2xl">👥</div>
                  <p className="text-sm font-sans text-dark-1">Présentiel</p>
                </div>
                <div className="bg-white rounded-lg p-3 text-center">
                  <div className="text-2xl">🛠️</div>
                  <p className="text-sm font-sans text-dark-1">Ateliers pratiques</p>
                </div>
                <div className="bg-white rounded-lg p-3 text-center">
                  <div className="text-2xl">🎯</div>
                  <p className="text-sm font-sans text-dark-1">Projets concrets</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Objectif */}
        <div className="bg-dark-2 rounded-2xl p-8 md:p-12 text-center text-white mb-16">
          <h3 className="text-2xl font-bold mb-4 font-sans">🎯 Objectif</h3>
          <p className="text-xl text-light-3 max-w-4xl mx-auto font-sans leading-relaxed">
            Révéler les futur·es inventeur·rices et créateur·rices, transmettre la culture de l'innovation 
            et développer des compétences techniques et transversales pour répondre aux défis actuels et futurs.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Compétences */}
          <div>
            <h3 className="text-xl font-bold text-dark-1 mb-6 font-sans">Compétences à acquérir</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                "Inventivité & créativité", "Collaboration & travail en équipe", 
                "Résolution de problèmes", "Partage & communication", "Résilience",
                "Design Thinking", "Esprit critique", "Autonomie & adaptabilité",
                "Prise de parole en public", "Gestion de projet agile", 
                "Culture maker et fabrication rapide"
              ].map((skill, index) => (
                <div key={index} className="flex items-center bg-light-4 rounded-lg p-3">
                  <div className="w-6 h-6 bg-green-1 rounded-full flex items-center justify-center text-dark-2 text-sm font-bold mr-3 flex-shrink-0">
                    ✓
                  </div>
                  <span className="text-dark-1 text-sm font-sans">{skill}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Thématiques */}
          <div>
            <h3 className="text-xl font-bold text-dark-1 mb-6 font-sans">Thématiques / Domaines</h3>
            <div className="space-y-3">
              {[
                "CAO / FAO (Conception et Fabrication Assistées par Ordinateur)",
                "Scan 3D et rétro-ingénierie",
                "Design mécanique",
                "Conception et design machine",
                "Découpe numérique (laser, vinyle, CNC)",
                "Usinage numérique (fraiseuse CNC, découpe bois/métal)",
                "Impression 3D (filament, résine, matériaux avancés)",
                "Moulage & coulage",
                "Composites et matériaux alternatifs",
                "Fabrication et production électronique (PCB, soudure)",
                "Conception de circuits",
                "Programmation embarquée (Arduino, ESP32, Raspberry Pi)",
                "Capteurs et dispositifs d'entrée",
                "Actionneurs et dispositifs de sortie",
                "Interfaces & applications connectées",
                "Réseaux et communication (IoT)",
                "Principes & pratiques de l'innovation",
                "Étude d'impact et implications sociales/environnementales",
                "Gestion de la propriété intellectuelle & droit d'auteur",
                "Modèles économiques et génération de revenus"
              ].map((topic, index) => (
                <div key={index} className="flex items-start">
                  <div className="w-2 h-2 bg-purple-1 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <span className="text-dark-1 text-sm font-sans">{topic}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="text-center mt-16">
          <button className="border-2 border-red-4 bg-red-4 hover:bg-transparent text-white hover:text-red-4 font-sans font-medium py-3 px-8 rounded-full transition-colors duration-300 mr-4">
            Découvrir le programme complet
          </button>
          <button className="border-2 border-purple-1 text-purple-1 hover:bg-purple-1 hover:text-white font-sans font-medium py-3 px-8 rounded-full transition-colors duration-300">
            Télécharger le syllabus
          </button>
        </div>
      </div>
    </section>
  );
};

export default MethodologySection;